import { Box, Grid, Stack, Typography } from "@mui/material";
import type { ReactNode } from "react";

import { LearningMedia, LearningTemplatePage } from "../../../lib/learning/contentContract";
import { resolveLearningMediaUrl } from "../../../lib/learning/mediaReference";
import { sanitizeLearningHtml } from "../../../lib/learning/sanitizeHtml";
import { borderRadius, color, space } from "../../../theme/designTokens";

const TemplateImage = ({ media, caption }: { media: LearningMedia; caption: string | null }) => (
  <Box component="figure" m={0}>
    <Box
      component="img"
      src={resolveLearningMediaUrl(media.fn)}
      alt={media.alt ?? ""}
      sx={{ display: "block", width: "100%", maxHeight: 520, objectFit: "cover", borderRadius: borderRadius.md }}
    />
    {caption && (
      <Typography component="figcaption" variant="caption" color="text.secondary" mt={space.sm}>
        {caption}
      </Typography>
    )}
  </Box>
);

const RichText = ({ html }: { html: string | null }) => {
  if (!html) return null;
  const sanitized = sanitizeLearningHtml(html);
  return (
    <Box
      data-testid="learning-template-rich-text"
      sx={{
        color: color.typographyBase,
        lineHeight: 1.7,
        "& > :first-of-type": { mt: 0 },
        "& > :last-child": { mb: 0 },
        "& a": { color: color.secondary },
      }}
      dangerouslySetInnerHTML={{ __html: sanitized }}
    />
  );
};

const SplitTemplate = ({ page, imageFirst }: { page: LearningTemplatePage; imageFirst: boolean }) => {
  const media = page.mediaWithCaption?.[0];
  if (!media) return null;
  const image = (
    <Grid item xs={12} md={6}>
      <TemplateImage media={media.media} caption={media.caption} />
    </Grid>
  );
  const text = (
    <Grid item xs={12} md={6} display="flex" alignItems="center">
      <RichText html={page.text} />
    </Grid>
  );
  return (
    <Grid container spacing={space.xxl} alignItems="stretch">
      {imageFirst ? image : text}
      {imageFirst ? text : image}
    </Grid>
  );
};

export const TemplateBlock = ({ page }: { page: LearningTemplatePage }) => {
  const media = page.mediaWithCaption ?? [];
  let content: ReactNode;

  switch (page.kind) {
    case "textOnly":
    case "singlePage":
      content = <RichText html={page.text} />;
      break;
    case "imageFullscreen":
      content = media[0] ? <TemplateImage media={media[0].media} caption={media[0].caption} /> : null;
      break;
    case "twoImagesFullscreen":
      content = (
        <Grid container spacing={space.xl}>
          {media.map(item => (
            <Grid item xs={12} md={6} key={item.media.fn}>
              <TemplateImage media={item.media} caption={item.caption} />
            </Grid>
          ))}
        </Grid>
      );
      break;
    case "imageLeftTextRight":
      content = <SplitTemplate page={page} imageFirst />;
      break;
    case "imageRightTextLeft":
      content = <SplitTemplate page={page} imageFirst={false} />;
      break;
    case "floatingImageLeft":
    case "floatingImageRight": {
      const item = media[0];
      content = item ? (
        <Box sx={{ display: "flow-root" }}>
          <Box
            sx={{
              width: { xs: "100%", md: "42%" },
              float: { md: page.kind === "floatingImageLeft" ? "left" : "right" },
              mr: { md: page.kind === "floatingImageLeft" ? space.xxl : 0 },
              ml: { md: page.kind === "floatingImageRight" ? space.xxl : 0 },
              mb: space.xl,
            }}
          >
            <TemplateImage media={item.media} caption={item.caption} />
          </Box>
          <RichText html={page.text} />
        </Box>
      ) : null;
      break;
    }
  }

  return (
    <Stack data-testid={`learning-template-${page.kind}`} spacing={space.xl}>
      {page.title && (
        <Typography component="h2" variant="h5">
          {page.title}
        </Typography>
      )}
      {content}
    </Stack>
  );
};
